import { PlayCircleOutlined } from "@ant-design/icons";
import { memo, useMemo } from "react";
import { Handle, Position, useStore } from "reactflow";
import ExtendingConnector from "./ExtendingConnector";
import { useNodeCreation } from "./useNodeCreation";
import baseStyles from "./BaseNode.module.css";

interface StartNodeData {
  label: string;
  title: string;
}

interface StartNodeProps {
  data: StartNodeData;
  id: string;
  selected?: boolean;
}

const NODE_STYLES = {
  headerBackground: "rgba(139, 92, 246, 0.1)",
  iconColor: "#8b5cf6",
  handleColor: "#8b5cf6"
};

/**
 * StartNode - A special "zero node" that indicates the beginning of a vision workflow.
 * This node is always present, cannot be deleted, and serves as the visual starting point.
 * It has no functional purpose other than connecting to the first actual node in the workflow.
 */
const StartNode = ({ data, id, selected }: StartNodeProps) => {
  const { createConnectedNode } = useNodeCreation(id);

  // Check if source handle is connected - using useStore for reactivity
  const isSourceConnected = useStore(useMemo(() => (state) => state.edges.some((edge) => edge.source === id), [id]));

  const handleNodeSelect = (selectedNodeType: string) => {
    createConnectedNode(selectedNodeType);
  };

  const shouldShowConnector = !isSourceConnected || selected;
  const isCompactMode = isSourceConnected && selected;
  return (
    <div
      className={`${baseStyles.nodeContainer} ${selected ? baseStyles.selected : ""}`}
      style={{
        minWidth: "140px",
        maxWidth: "140px",
        opacity: 1
      }}
    >
      <div
        className={baseStyles.nodeHeader}
        style={{
          background: NODE_STYLES.headerBackground,
          cursor: "default" // Not draggable
        }}
      >
        <span className={baseStyles.nodeIcon} style={{ color: NODE_STYLES.iconColor }}>
          <PlayCircleOutlined />
        </span>
        <span className={baseStyles.nodeTitle}>{data.title || "Start"}</span>
      </div>

      {/* Output handle only - connects to first node */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: NODE_STYLES.handleColor,
          width: 10,
          height: 10,
          border: "2px solid white"
        }}
      />

      {/* Show extending connector if source handle is not connected OR node is selected */}
      {shouldShowConnector && (
        <ExtendingConnector nodeId={id} nodeType="startNode" onNodeSelect={handleNodeSelect} compact={isCompactMode} />
      )}
    </div>
  );
};

export default memo(StartNode);
